import { useEffect, useState, useRef } from "react"
import {
  Trophy, Target, Star, Loader2,
  Lock, CheckCircle, Medal, X, Gamepad2, Zap,
  TrendingUp, TrendingDown, MessageCircle, ShieldCheck,
} from "lucide-react"
import { studentService, type GamificationData } from "@/services/studentService"
import PasswordProtector from "@/components/StudentsView/Games/PasswordProtector"
import CyberbullyDetection from "@/components/StudentsView/Games/CyberBullyDetection"
import { safeLog } from '@/utils/safeLog';

const GAME_MODAL_MAP: Record<string, string> = {
  'password protector':   'password',
  'cyber detective':      'cyber',
  'cyberbully detection': 'cyber',
}

function resolveGameModal(name: string): string | null {
  return GAME_MODAL_MAP[name.toLowerCase()] ?? null
}

export function StudentGameboard() {
  const [data, setData]                     = useState<GamificationData | null>(null)
  const [loading, setLoading]               = useState(true)
  const [error, setError]                   = useState<string | null>(null)
  const [completingGame, setCompletingGame] = useState<string | null>(null)
  const [activeGame, setActiveGame]         = useState<string | null>(null)
  const [activeGameId, setActiveGameId]     = useState<string | null>(null)
  const prevPointsRef                       = useRef<number | null>(null)
  const [pointsDelta, setPointsDelta]       = useState<number>(0)
  const [dm, setDm]                         = useState(() => localStorage.getItem('studentDarkMode') === 'true')

  const [bestScores, setBestScores] = useState<Record<string, number>>(() => {
    try { return JSON.parse(localStorage.getItem('sh_game_best_scores') || '{}') } catch { return {} }
  })

  useEffect(() => {
    const handler = (e: Event) => setDm((e as CustomEvent<{ darkMode: boolean }>).detail.darkMode)
    window.addEventListener('studentDarkModeChanged', handler)
    return () => window.removeEventListener('studentDarkModeChanged', handler)
  }, [])

  useEffect(() => { fetchGamification() }, [])

  const fetchGamification = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await studentService.getGamification()
      if (prevPointsRef.current !== null) {
        setPointsDelta(result.points - prevPointsRef.current)
      }
      prevPointsRef.current = result.points
      setData(result)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  const recordBestScore = (gameId: string, score: number) => {
    setBestScores(prev => {
      const updated = { ...prev }
      if (!updated[gameId] || score > updated[gameId]) {
        updated[gameId] = score
        localStorage.setItem('sh_game_best_scores', JSON.stringify(updated))
      }
      return updated
    })
  }

  const handlePlayGame = async (gameId: string, gameName: string) => {
    const modal = resolveGameModal(gameName)
    if (modal) {
      setActiveGameId(gameId)
      setActiveGame(modal)
    } else {
      try {
        setCompletingGame(gameId)
        const result = await studentService.completeGame(gameId)
        if (result.pointsEarned) recordBestScore(gameId, result.pointsEarned)
        await fetchGamification()
        alert(result.message)
      } catch (err: unknown) {
        safeLog.error('Failed to complete game:', err)
      } finally {
        setCompletingGame(null)
      }
    }
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center animate-pulse ${dm ? 'bg-white/10 border border-white/10' : 'bg-indigo-100'}`}>
            <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
          </div>
          <span className={`text-sm ${dm ? 'text-white/60' : 'text-gray-500'}`}>Loading games…</span>
        </div>
      </div>
    )
  }

  const progressPct     = data?.progressToNextLevel ?? 0
  const pointsRemaining = data ? Math.max(0, (data.nextLevelPoints) - data.points) : 0

  return (
    <div className="px-4 py-5 space-y-4">
      {error && (
        <div className={`border px-4 py-3 rounded-2xl text-sm ${dm ? 'bg-amber-900/30 border-amber-500/20 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
          Using cached data — live sync unavailable
        </div>
      )}

      {/* ── Points / Level hero ── */}
      <div className="relative overflow-hidden rounded-3xl p-5 text-white shadow-xl" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 45%, #fb923c 100%)' }}>
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none" style={{ background: 'rgba(255,255,255,0.12)' }} />
        <div className="absolute -bottom-8 -left-6 w-32 h-32 rounded-full pointer-events-none" style={{ background: 'rgba(255,255,255,0.08)' }} />
        <div className="absolute top-4 right-16 w-16 h-16 rounded-full pointer-events-none" style={{ background: 'rgba(255,255,255,0.07)' }} />

        <div className="relative flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-inner" style={{ background: 'rgba(255,255,255,0.22)', border: '1.5px solid rgba(255,255,255,0.3)' }}>
            <Trophy className="w-8 h-8 text-white drop-shadow" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/70 text-[11px] font-semibold uppercase tracking-widest mb-1">Your Progress</p>
            <div className="flex items-center gap-2 mb-2">
              <span
                className="text-4xl font-black leading-none drop-shadow-sm"
                style={{ background: 'linear-gradient(160deg, #fff7a0 0%, #ffe45e 50%, #fbbf24 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
              >
                {(data?.points ?? 0).toLocaleString()}
              </span>
              <span className="text-white/60 text-sm font-semibold mt-1">pts</span>
              {pointsDelta > 0 && (
                <span className="flex items-center gap-0.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(52,211,153,0.25)', color: '#6ee7b7', border: '1px solid rgba(52,211,153,0.35)' }}>
                  <TrendingUp className="w-3 h-3" />+{pointsDelta}
                </span>
              )}
              {pointsDelta < 0 && (
                <span className="flex items-center gap-0.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(251,113,133,0.25)', color: '#fda4af', border: '1px solid rgba(251,113,133,0.35)' }}>
                  <TrendingDown className="w-3 h-3" />{pointsDelta}
                </span>
              )}
            </div>
            <span className="inline-flex items-center gap-1 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md" style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)', boxShadow: '0 2px 8px rgba(99,102,241,0.45)' }}>
              ✦ Level {data?.level ?? 1} · {data?.levelName ?? 'Digital Beginner'}
            </span>
          </div>
        </div>

        <div className="relative mt-5">
          <div className="flex justify-between mb-1.5">
            <span className="text-xs font-semibold text-white/80">Level {data?.level ?? 1} → Level {(data?.level ?? 1) + 1}</span>
            <span className="text-xs font-bold text-white/90">{progressPct}%</span>
          </div>
          <div className="h-3 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.15)' }}>
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${progressPct}%`, background: 'linear-gradient(90deg, #34d399, #10b981)', boxShadow: '0 0 8px rgba(52,211,153,0.6)' }}
            />
          </div>
          <p className="text-[11px] font-medium text-white/70 mt-1.5">
            {pointsRemaining > 0
              ? `${pointsRemaining} more pts to reach ${data?.levelName ? `Level ${(data.level) + 1}` : 'next level'}`
              : 'Level up reached! 🎉'
            }
          </p>
        </div>
      </div>

      {/* ── Weekly challenge ── */}
      {data?.weeklyChallenge && (
        <div className={`rounded-2xl border p-4 ${
          data.weeklyChallenge.isComplete
            ? dm ? 'bg-emerald-900/40 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'
            : dm ? 'bg-purple-900/40 border-purple-500/30'  : 'bg-purple-50 border-purple-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Target className={`w-5 h-5 ${data.weeklyChallenge.isComplete ? 'text-emerald-500' : 'text-purple-500'}`} />
              <p className={`font-semibold text-sm ${
                data.weeklyChallenge.isComplete
                  ? dm ? 'text-emerald-300' : 'text-emerald-800'
                  : dm ? 'text-purple-300'  : 'text-purple-800'
              }`}>
                Weekly Challenge
              </p>
            </div>
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
              data.weeklyChallenge.isComplete
                ? 'bg-emerald-200 text-emerald-800'
                : 'bg-purple-200 text-purple-800'
            }`}>
              +{data.weeklyChallenge.bonusPoints} pts bonus
            </span>
          </div>
          <p className={`text-xs mb-3 ${
            data.weeklyChallenge.isComplete
              ? dm ? 'text-emerald-400' : 'text-emerald-600'
              : dm ? 'text-purple-400'  : 'text-purple-600'
          }`}>
            {data.weeklyChallenge.title} — {data.weeklyChallenge.description}
          </p>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-white/60 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${data.weeklyChallenge.isComplete ? 'bg-emerald-500' : 'bg-purple-500'}`}
                style={{ width: `${Math.min((data.weeklyChallenge.completed / data.weeklyChallenge.target) * 100, 100)}%` }}
              />
            </div>
            <span className={`text-xs font-semibold flex-shrink-0 ${
              data.weeklyChallenge.isComplete
                ? dm ? 'text-emerald-400' : 'text-emerald-700'
                : dm ? 'text-purple-400'  : 'text-purple-700'
            }`}>
              {data.weeklyChallenge.completed}/{data.weeklyChallenge.target}
            </span>
          </div>
          {data.weeklyChallenge.isComplete && (
            <p className={`text-xs font-semibold mt-2 ${dm ? 'text-emerald-400' : 'text-emerald-700'}`}>✅ Challenge Complete!</p>
          )}
        </div>
      )}

      {/* ── Games ── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${dm ? 'bg-blue-900/40' : 'bg-blue-100'}`}>
            <Gamepad2 className="w-4 h-4 text-blue-500" />
          </div>
          <h2 className={`font-semibold ${dm ? 'text-white' : 'text-gray-900'}`}>
            Games ({data?.games?.length ?? 0})
          </h2>
        </div>

        <div className="space-y-3">
          {data?.games && data.games.length > 0 ? (
            [...data.games]
              .sort((a, b) => {
                const aPlayable = resolveGameModal(a.name) !== null ? 0 : 1
                const bPlayable = resolveGameModal(b.name) !== null ? 0 : 1
                return aPlayable - bPlayable
              })
              .map(game => {
                const hasModal   = resolveGameModal(game.name) !== null
                const done       = hasModal && game.status === 'completed'
                const inProgress = hasModal && game.status === 'in_progress'
                const best       = hasModal ? (bestScores[game.id] ?? (done ? game.pointsEarned : 0)) : 0
                return (
                  <div
                    key={game.id}
                    className={`rounded-2xl border p-4 transition-all ${
                      !hasModal
                        ? dm ? 'bg-white/5 border-white/5 opacity-60' : 'bg-gray-50 border-gray-100 opacity-70'
                        : inProgress
                        ? dm ? 'bg-blue-900/30 border-blue-500/30' : 'bg-blue-50 border-blue-200'
                        : dm ? 'bg-white/10 border-white/10 shadow-sm' : 'bg-white border-gray-100 shadow-sm'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={
                            !hasModal   ? { background: dm ? 'rgba(255,255,255,0.05)' : '#f1f5f9' } :
                            done        ? { background: 'linear-gradient(135deg, #34d399, #10b981)' } :
                            inProgress  ? { background: dm ? 'rgba(59,130,246,0.3)' : '#dbeafe' } :
                                          { background: dm ? 'rgba(99,102,241,0.3)' : '#e0e7ff' }
                          }
                        >
                          {done
                            ? <CheckCircle className="w-5 h-5 text-white" />
                            : <Gamepad2    className={`w-5 h-5 ${hasModal ? 'text-indigo-500' : dm ? 'text-white/20' : 'text-gray-400'}`} />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold text-sm ${hasModal ? dm ? 'text-white' : 'text-gray-900' : dm ? 'text-white/30' : 'text-gray-400'}`}>{game.name}</p>
                          {game.description && (
                            <p className={`text-xs mt-0.5 line-clamp-2 ${dm ? 'text-white/50' : 'text-gray-500'}`}>{game.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <div className="flex items-center gap-1">
                              <Star className={`w-3.5 h-3.5 ${hasModal ? 'text-amber-500' : dm ? 'text-white/20' : 'text-gray-300'}`} />
                              <span className={`text-xs ${hasModal ? dm ? 'text-white/60' : 'text-gray-600' : dm ? 'text-white/30' : 'text-gray-400'}`}>{game.maxPoints} pts</span>
                            </div>
                            {best > 0 && (
                              <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full text-white" style={{ background: 'linear-gradient(135deg, #34d399, #10b981)' }}>
                                Best: {best} pts
                              </span>
                            )}
                            {inProgress && (
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${dm ? 'text-blue-300 bg-blue-900/40' : 'text-blue-600 bg-blue-100'}`}>
                                In Progress
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {hasModal ? (
                        <button
                          onClick={() => handlePlayGame(game.id, game.name)}
                          disabled={completingGame === game.id}
                          className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50 hover:opacity-90"
                          style={{
                            border: 'none', cursor: 'pointer',
                            background: done
                              ? 'linear-gradient(135deg, #34d399, #10b981)'
                              : inProgress
                                ? 'linear-gradient(135deg, #60a5fa, #3b82f6)'
                                : 'linear-gradient(135deg, #818cf8, #6366f1)',
                          }}
                        >
                          {completingGame === game.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : done ? 'Play Again' : inProgress ? 'Continue' : 'Play'
                          }
                        </button>
                      ) : (
                        <span className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-xl ${dm ? 'text-white/30 bg-white/5' : 'text-gray-400 bg-gray-100'}`}>
                          Coming Soon
                        </span>
                      )}
                    </div>
                  </div>
                )
              })
          ) : (
            <div className={`text-center py-10 ${dm ? 'text-white/40' : 'text-gray-400'}`}>
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${dm ? 'bg-white/10' : 'bg-gray-100'}`}>
                <Target className={`w-7 h-7 ${dm ? 'text-white/20' : 'text-gray-300'}`} />
              </div>
              <p className="text-sm">No games available yet</p>
              <p className={`text-xs mt-1 ${dm ? 'text-white/30' : 'text-gray-300'}`}>Check back soon!</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Achievements ── */}
      {data?.achievements && data.achievements.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${dm ? 'bg-purple-900/40' : 'bg-purple-100'}`}>
              <Medal className="w-4 h-4 text-purple-500" />
            </div>
            <h2 className={`font-semibold ${dm ? 'text-white' : 'text-gray-900'}`}>Achievements</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {data.achievements.map(achievement => (
              <div
                key={achievement.id}
                className={`rounded-2xl border p-3 text-center transition-all ${
                  achievement.unlocked
                    ? dm ? 'bg-purple-900/40 border-purple-500/30' : 'bg-purple-50 border-purple-200'
                    : dm ? 'bg-white/5 border-white/5 opacity-50'  : 'bg-gray-50 border-gray-100 opacity-50'
                }`}
              >
                <div className="text-2xl mb-1">{achievement.icon}</div>
                <p className={`text-xs font-semibold leading-tight ${dm ? 'text-white' : 'text-gray-800'}`}>
                  {achievement.name}
                </p>
                <p className={`text-[10px] mt-0.5 leading-tight line-clamp-2 ${dm ? 'text-white/50' : 'text-gray-500'}`}>
                  {achievement.description}
                </p>
                <div className="mt-2 flex justify-center">
                  {achievement.unlocked
                    ? <CheckCircle className="w-4 h-4 text-purple-500" />
                    : <Lock        className={`w-4 h-4 ${dm ? 'text-white/20' : 'text-gray-300'}`} />
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Leaderboard ── */}
      {data?.leaderboard && data.leaderboard.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${dm ? 'bg-amber-900/40' : 'bg-amber-100'}`}>
              <Zap className="w-4 h-4 text-amber-500" />
            </div>
            <h2 className={`font-semibold ${dm ? 'text-white' : 'text-gray-900'}`}>Leaderboard</h2>
          </div>
          <div className={`rounded-2xl border overflow-hidden ${dm ? 'bg-white/10 border-white/10' : 'bg-white border-gray-100 shadow-sm'}`}>
            {data.leaderboard.map((entry, idx) => {
              const medal = entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : null
              return (
                <div
                  key={entry.rank}
                  className={`flex items-center gap-3 px-4 py-3 ${
                    idx < data.leaderboard.length - 1 ? dm ? 'border-b border-white/10' : 'border-b border-gray-50' : ''
                  } ${entry.isCurrentUser ? dm ? 'bg-indigo-900/40' : 'bg-indigo-50' : ''}`}
                >
                  <span className="w-8 text-center text-sm font-bold flex-shrink-0">
                    {medal ?? <span className={dm ? 'text-white/40' : 'text-gray-400'}>#{entry.rank}</span>}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${entry.isCurrentUser ? 'text-indigo-400' : dm ? 'text-white' : 'text-gray-900'}`}>
                      {entry.isCurrentUser ? entry.name + ' (You)' : 'Classmate'}
                    </p>
                    <p className={`text-[10px] ${dm ? 'text-white/40' : 'text-gray-400'}`}>Level {entry.level}</p>
                  </div>
                  <span className={`text-sm font-bold flex-shrink-0 ${dm ? 'text-white' : 'text-gray-700'}`}>
                    {entry.points.toLocaleString()}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="h-2" />

      {/* ── Game modal overlay ── */}
      {activeGame && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 px-0 sm:px-4"
          style={{ backdropFilter: 'blur(4px)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div
              className="px-5 pt-5 pb-4 flex items-center justify-between"
              style={{ background: 'linear-gradient(135deg, #6366f1, #3b82f6)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  {activeGame === 'password'
                    ? <ShieldCheck className="w-5 h-5 text-white" />
                    : <MessageCircle className="w-5 h-5 text-white" />
                  }
                </div>
                <div>
                  <h2 className="text-white font-bold text-base leading-tight">
                    {activeGame === 'password' ? 'Password Protector' : 'Cyberbully Detection'}
                  </h2>
                  <p className="text-white/60 text-xs">Interactive safety game</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setActiveGame(null); fetchGamification() }}
                className="w-8 h-8 flex items-center justify-center rounded-full transition hover:bg-white/20 focus:outline-none"
                aria-label="Close game"
                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer' }}
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto p-5">
              {activeGame === 'password' && (
                <PasswordProtector
                  onComplete={async (score, maxScore) => {
                    if (!activeGameId) return
                    const pts = Math.round((score / maxScore) * (data?.games?.find(g => g.id === activeGameId)?.maxPoints ?? 50))
                    try {
                      const result = await studentService.completeGame(activeGameId, pts)
                      if (result.bestScore) recordBestScore(activeGameId, result.bestScore)
                      await fetchGamification()
                    } catch { /* non-fatal */ }
                  }}
                />
              )}
              {activeGame === 'cyber' && (
                <CyberbullyDetection
                  onComplete={async (score, maxScore) => {
                    if (!activeGameId) return
                    const pts = Math.round((score / maxScore) * (data?.games?.find(g => g.id === activeGameId)?.maxPoints ?? 100))
                    try {
                      const result = await studentService.completeGame(activeGameId, pts)
                      if (result.bestScore) recordBestScore(activeGameId, result.bestScore)
                      await fetchGamification()
                    } catch { /* non-fatal */ }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
